import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, MessageCircle, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { postLead } from "@/lib/leads";
import { resolveAssetPath } from "@/lib/assets";

const FieldWrap = ({ label, children }) => (
  <label className="block relative">
    <span className="absolute -top-2 left-3 bg-white px-1 text-[11px] text-gray-500">{label}</span>
    {children}
  </label>
);

const PhoneInput = ({ value, onChange }) => (
  <FieldWrap label="Mobile No.*">
    <div className="flex items-center border border-gray-300 rounded-md focus-within:border-[var(--yellow-2)] transition">
      <span className="flex items-center gap-1.5 px-3 border-r border-gray-200 text-sm text-[var(--navy)]">
        <img src={resolveAssetPath("/assets/flags/in.png")} width="20" height="14" alt="India" className="rounded-sm" />
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </span>
      <input
        type="tel"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
        placeholder="+91"
        className="flex-1 bg-transparent outline-none px-3 py-2.5 text-sm"
        data-testid="enquiry-mobile"
      />
    </div>
  </FieldWrap>
);

export const EnquiryDrawer = ({ open, onOpenChange, tourTitle = "" }) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", desc: tourTitle, wa: true });
  const [more, setMore] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async () => {
    if (!form.name.trim() || form.mobile.length < 10 || !form.email.includes("@")) {
      toast.error("Please fill your name, a valid 10-digit mobile and email.");
      return;
    }
    try {
      await postLead({
        name: form.name,
        email: form.email,
        phone: form.mobile,
        destination: tourTitle,
        message: form.desc,
        source: "enquiry",
      });
      toast.success("Enquiry received! Our travel expert will reach out shortly.");
      onOpenChange(false);
    } catch (e) {
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="enquiry-drawer">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="text-[var(--navy)] tracking-wide">QUICK ENQUIRY</SheetTitle>
          <SheetDescription>Share your details and our travel expert will call you back shortly.</SheetDescription>
        </SheetHeader>
        <div className="px-6 flex-1 overflow-y-auto thin-scroll space-y-5 pb-4">
          <FieldWrap label="Full Name*">
            <Input value={form.name} onChange={set("name")} placeholder="Full Name*" className="py-5" data-testid="enquiry-name" />
          </FieldWrap>
          <PhoneInput value={form.mobile} onChange={set("mobile")} />
          <FieldWrap label="Email ID*">
            <Input type="email" value={form.email} onChange={set("email")} placeholder="Email ID*" className="py-5" data-testid="enquiry-email" />
          </FieldWrap>
          <FieldWrap label="Drop us a small description">
            <Textarea value={form.desc} onChange={set("desc")} rows={2} className="resize-none" />
          </FieldWrap>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox checked={form.wa} onCheckedChange={(v) => setForm((f) => ({ ...f, wa: !!v }))} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
            <span className="text-[13px] text-[var(--navy)]">Receive itinerary on</span>
            <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-[13px]"><MessageCircle className="w-4 h-4" /> WhatsApp</span>
          </label>
          <div className="border-t border-dashed border-gray-200 pt-4">
            <p className="text-[12px] text-gray-500">Would you like to share more info? It will help us curate the best tours for you. <span className="italic">(Optional)</span></p>
            <button type="button" onClick={() => setMore((v) => !v)} className="mt-2 inline-flex items-center gap-1 font-semibold text-[var(--navy)] text-[13px]">
              Additional Details <ChevronDown className={`w-4 h-4 transition-transform ${more ? "rotate-180" : ""}`} />
            </button>
            {more && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Input placeholder="Travel month" className="py-5" />
                <Input placeholder="No. of travellers" className="py-5" />
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-[var(--soft)]">
          <Button onClick={submit} data-testid="enquiry-submit" className="w-full bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold py-5 rounded-md">
            Submit Enquiry
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Compact "Want us to call you?" card used on the listing intro
export const CallbackCard = () => {
  const [form, setForm] = useState({ name: "", mobile: "" });
  const submit = async () => {
    if (!form.name.trim() || form.mobile.length < 10) {
      toast.error("Enter your name and a valid 10-digit mobile number.");
      return;
    }
    try {
      await postLead({
        name: form.name,
        phone: form.mobile,
        message: "Requested a call back",
        source: "callback",
      });
      toast.success("Thanks! We'll call you back within 24 hours.");
      setForm({ name: "", mobile: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };
  return (
    <div className="bg-gradient-to-br from-white to-[var(--soft)] border border-gray-200 rounded-2xl p-5 shadow-[0_20px_40px_-24px_rgba(11,37,69,0.4)]" data-testid="callback-card">
      <div className="text-[15px] font-semibold text-[var(--navy)] mb-4">Want us to call you?</div>
      <div className="space-y-4">
        <FieldWrap label="Full Name*">
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full Name*" className="py-5" data-testid="callback-name" />
        </FieldWrap>
        <label className="block relative">
          <span className="absolute -top-2 left-3 bg-white px-1 text-[11px] text-gray-500">Mobile No.*</span>
          <div className="flex items-center border border-gray-300 rounded-md focus-within:border-[var(--yellow-2)] transition">
            <span className="flex items-center gap-1.5 px-3 border-r border-gray-200 text-sm">
              <img src={resolveAssetPath("/assets/flags/in.png")} width="20" height="14" alt="India" className="rounded-sm" />
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            </span>
            <input
              type="tel"
              value={form.mobile}
              onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/[^0-9]/g, "") }))}
              placeholder="+91"
              className="flex-1 bg-transparent outline-none px-3 py-2.5 text-sm"
              data-testid="callback-mobile"
            />
          </div>
        </label>
        <Button onClick={submit} data-testid="callback-submit" className="w-full bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold py-5 rounded-md inline-flex items-center gap-2">
          <Phone className="w-4 h-4" /> Request Call Back
        </Button>
      </div>
    </div>
  );
};
